/* eslint-disable no-restricted-syntax */
import './Table.scss';

import { Box, Flex, Image, Skeleton, Text } from '@chakra-ui/react';
import { ReactGrid } from '@silevis/reactgrid';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { AddRowCellTemplate } from 'components/AddRowCell';
import { DateCellTemplate } from 'components/DateCell';
import DeleteGroupModal from 'components/DeleteGroupModal';
import { DeleteRowCellTemplate } from 'components/DeleteRowCell';
import { DropdownCellTemplate } from 'components/DropdownCell';
import FullScreenLoader from 'components/FullScreenLoader';
import { HeaderRowTemplate } from 'components/HeaderRow';
import PlanPermission from 'container/PlanPermission';
import { HEADER_HEIGHT_WITH_NAVIGATION, getCurrencySymbol } from 'projectConstants';
// import { FetchPlangPageIndicatorTableData } from 'redux/PlanPageSlice';
import {
  AddIndicator,
  DeleteIndicator,
  FetchBlockOutputs,
  FetchFormulaList,
  GetDependencyIndicator,
  ReorderIndicator,
  UpdateIndicator,
  getAddIndicator,
  getBaseScenario,
  getDimensionalIndicator,
  getIndicatorHeaders,
  getPlannerBlock,
  getPlannerModel,
  getSelectedCell,
  getBlockOutputsLoading,
  getBlockOutputsError,
  handleLocallyAddIndicator,
  // handleAddIndicator,
  handleSelectedCell,
  handleSelectedCellLocally,
} from 'redux/PlannerModeSlice';
import { getExandSideInputStatus } from 'redux/BuilderModeSlice';
import { UpdateBlock } from 'redux/ModelsSlice';
import addAction from 'assets/add_action_icon.png';
import { ChevronCellTemplate } from 'components/ChevronCell/ChevronwithEsc';
import { TextCellTemplateOpz } from 'pages/PlanPageDetailsOPZ/Inputs/IndicatorPageMultiDim/TextCellTemplateOpz';

import {
  buildTree,
  getDimensionalColumns,
  getDimensionalRows,
  getExpandedRows,
  handleCanReorderRows,
  handleCellChange,
  handleColumnResize,
  reorderArray,
} from './TableHelpersOpz';

// Helper function to get comma-separated dimension IDs (excluding 'Time')
function getDimensionIdsString(plannerBlock: any): string {
  if (!plannerBlock?.dimensions) return '';
  return plannerBlock.dimensions
    .filter((item: any) => item.name !== 'Time')
    .map((item: any) => item.id)
    .join(', ');
}

const BuilderModeDataTableOpz = ({ planPermission }: any) => {
  const { modelId, blockId } = useParams();
  const dispatch: any = useDispatch();
  const tableReference: any = useRef(null);
  const selectedCell = useSelector(getSelectedCell);
  const plannerBlock = useSelector(getPlannerBlock);
  const baseScenario = useSelector(getBaseScenario);
  const addIndicator = useSelector(getAddIndicator);
  const modelDetails = useSelector(getPlannerModel);
  const indicatorHeaders = useSelector(getIndicatorHeaders);
  const indicatorValues = useSelector(getDimensionalIndicator);
  const isExpand = useSelector(getExandSideInputStatus);
  const blockOutputsLoading = useSelector(getBlockOutputsLoading);
  const blockOutputsError = useSelector(getBlockOutputsError);
  const [tableData, setTableData]: any = useState([]);
  const [deleteModal, setDeleteModal] = useState({
    indicatorId: '',
    indicatorName: '',
    status: false,
  });
  const [gridProperties, setGridProperties] = useState({});
  const [addRowWorker, setAddRowWorker] = useState<Worker | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const clickCountReference = useRef(0);
  const [isQueueProcessing, setIsQueueProcessing] = useState(false);
  const messageQueueReference = useRef<any[]>([]);
  const fetchIndicatorsRequestReference = useRef<NodeJS.Timeout>();
  const processingTimeoutReference = useRef<NodeJS.Timeout>();
  const baseScenarioReference = useRef(baseScenario);

  const processedTableData = useMemo(() => {
    if (!tableData?.length || !indicatorHeaders?.length) return [];
    const currencySymbol = getCurrencySymbol(modelDetails?.reporting_currency);
    return getDimensionalRows(tableData, indicatorHeaders, currencySymbol);
  }, [tableData, indicatorHeaders, modelDetails?.reporting_currency]);

  const memorizedRows = useMemo(() => {
    if (processedTableData.length === 0) return [];
    return buildTree(processedTableData);
  }, [processedTableData]);

  const memorizedColumns: any = useMemo(() => {
    return (
      indicatorHeaders?.length >= 0 &&
      plannerBlock &&
      getDimensionalColumns(indicatorHeaders, tableReference?.current?.offsetWidth, plannerBlock)
    );
  }, [indicatorHeaders, isExpand, plannerBlock]);

  const [columns, setColumns]: any = useState(memorizedColumns);
  const [rows, setRows]: any = useState(memorizedRows);
  const [rowsToRender, setRowsToRender]: any = useState(getExpandedRows(rows));
  const createEmptyRow = useMemo(() => {
    if (!indicatorHeaders?.length) return {};

    const object: Record<string, number> = indicatorHeaders.reduce(
      (accumulator: Record<string, number>, header: any) => {
        if (header?.name) {
          accumulator[header.name] = 0;
        }
        return accumulator;
      },
      {},
    );
    return object;
  }, [indicatorHeaders]);

  useEffect(() => {
    if (indicatorValues?.length >= 0) setTableData(indicatorValues || []);
  }, [indicatorValues]);

  useEffect(() => {
    if (memorizedColumns?.length >= 0) setColumns(memorizedColumns);
  }, [memorizedColumns]);

  useEffect(() => {
    if (tableReference?.current && indicatorHeaders?.length >= 0 && plannerBlock) {
      setColumns(
        getDimensionalColumns(indicatorHeaders, tableReference?.current?.offsetWidth, plannerBlock),
      );
    }
  }, [tableReference?.current, plannerBlock]);

  useEffect(() => {
    if (memorizedRows?.length >= 0) setRows(memorizedRows);
  }, [memorizedRows]);

  useEffect(() => {
    if (rows?.length > 0) {
      const expandedRows = getExpandedRows(rows);
      if (JSON.stringify(expandedRows) !== JSON.stringify(rowsToRender)) {
        setRowsToRender(expandedRows);
      }
    }
  }, [rows]);

  // Process all updates in one batch
  const processUpdates = useCallback((updates: any[]) => {
    if (updates.length === 0) return;

    // Batch all state updates together
    requestAnimationFrame(() => {
      setRows((previousRows: any) => {
        const rowsWithoutFooter = previousRows.filter((row: any) => row.rowId !== 'footer');
        const rowsToUpdate = updates.map((update) => update.localNewRow);
        return [...rowsWithoutFooter, ...rowsToUpdate, updates[updates.length - 1].footerRow];
      });

      setTableData((previousData: any) => [
        ...previousData,
        ...updates.map((update) => update.baseRow),
      ]);

      // Only set focus after a small delay to ensure row exists
      setTimeout(() => {
        const lastUpdate = updates[updates.length - 1];
        if (lastUpdate?.rowId) {
          setGridProperties({
            focusLocation: { rowId: lastUpdate.rowId, columnId: 'name' },
          });
        }
      }, 50);

      setIsProcessing(false);
      clickCountReference.current = 0;
    });
  }, []);

  // Process message queue
  const processMessageQueue = useCallback(() => {
    if (messageQueueReference.current.length === 0) {
      setIsQueueProcessing(false);
      setIsProcessing(false);
      return;
    }

    const updates = [...messageQueueReference.current];
    messageQueueReference.current = [];

    processUpdates(updates);
    setIsQueueProcessing(false);
  }, [processUpdates]);

  // Initialize worker with optimized message handling
  useEffect(() => {
    const worker = new Worker(new URL('../../workers/addRowWorker.ts', import.meta.url), {
      type: 'module',
    });

    worker.addEventListener('message', (event) => {
      messageQueueReference.current.push(event.data);

      if (!isQueueProcessing) {
        setIsQueueProcessing(true);
        requestAnimationFrame(processMessageQueue);
      }
    });

    setAddRowWorker(worker);

    return () => {
      worker.terminate();
    };
  }, [isQueueProcessing, processMessageQueue]);

  // Optimized add row handler with debounce
  const handleAddRow = useCallback(() => {
    if (!addRowWorker || isProcessing) return;

    const currentClickCount = clickCountReference.current + 1;
    clickCountReference.current = currentClickCount;
    setIsProcessing(true);

    // Clear any existing grid properties to prevent focus errors
    setGridProperties({});

    // Clear existing timeout if any
    if (processingTimeoutReference.current) {
      clearTimeout(processingTimeoutReference.current);
    }

    addRowWorker.postMessage({
      tableData,
      createEmptyRow,
      indicatorHeaders,
    });

    // Clear processing state if no response within timeout
    processingTimeoutReference.current = setTimeout(() => {
      if (clickCountReference.current === currentClickCount) {
        setIsProcessing(false);
      }
    }, 1000);
  }, [addRowWorker, tableData, createEmptyRow, indicatorHeaders, isProcessing]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutReference.current) {
        clearTimeout(processingTimeoutReference.current);
      }
    };
  }, []);

  useEffect(() => {
    if (tableData?.length > 0) {
      const hasInvalidIds = tableData.some(
        (row: any) => Number.isNaN(Number.parseInt(row.id, 10)) || row.id === undefined,
      );

      if (hasInvalidIds) {
        const correctedData = tableData.map((row: any, index: number) => ({
          ...row,
          id: row.id || index,
          position: String(row.id || index),
        }));
        setTableData(correctedData);
      }
    }
  }, [tableData]);

  useEffect(() => {
    if (addIndicator) handleAddRow();
  }, [addIndicator]);

  // Keep ref updated with latest baseScenario
  useEffect(() => {
    baseScenarioReference.current = baseScenario;
  }, [baseScenario]);

  const handleDeleteRow = async (data: any) => {
    // Check if this is a local/empty row
    const rowToDelete = rows.find((row: any) => row.itemId === data?.id);
    if (rowToDelete?.isNewCell) {
      // For local/empty rows, just update the state directly
      setRows((previousRows: any[]) => previousRows.filter((row: any) => row.itemId !== data?.id));
      setTableData((previousData: any[]) => previousData.filter((row: any) => row.id !== data?.id));
      return;
    }
    // For existing rows, proceed with API call
    await dispatch(GetDependencyIndicator({ indicatorId: data?.id }));
    setDeleteModal({ indicatorId: data?.id, indicatorName: data?.itemName, status: true });
  };

  const handleFocusLocationChanged = async (data: any) => {
    if (selectedCell?.rowId === data?.rowId) return;

    const sourceRow = tableData.find((item: any) => {
      const itemRowId = item?.id ?? `${item?.name}_${item?.id ?? item?.parentId}`;
      return (
        String(itemRowId) === String(data?.rowId) ||
        Number(item?.id) === Number(data?.rowId) ||
        Number(item?.indicatorId) === Number(data?.rowId)
      );
    });

    await dispatch(
      handleSelectedCellLocally({
        ...data,
        ...(sourceRow && {}),
        id: sourceRow?.parentId ?? sourceRow?.id ?? data?.rowId,
        parentId: sourceRow?.parentId,
        indicatorId: sourceRow?.indicatorId ?? sourceRow?.parentId ?? sourceRow?.id,
      }),
    );
  };

  const fetchBlockOutputsIndicators = useCallback(() => {
    // Use ref to get the latest baseScenario value (handles async loading)
    const currentBaseScenario = baseScenarioReference.current;

    // Don't make API call if baseScenario is not loaded yet
    if (!currentBaseScenario?.id) {
      return;
    }

    const dimensionIds = getDimensionIdsString(plannerBlock);
    const payload = {
      blockId,
      params: {
        dim_id: dimensionIds,
        indicator_filter: 'all',
        scenario_id: currentBaseScenario.id,
      },
    };
    dispatch(FetchBlockOutputs(payload));
  }, [blockId, plannerBlock, dispatch]);

  const debouncedFetchIndicators = useCallback(() => {
    // Clear any pending timeout
    if (fetchIndicatorsRequestReference.current) {
      clearTimeout(fetchIndicatorsRequestReference.current);
    }
    fetchIndicatorsRequestReference.current = setTimeout(() => {
      fetchBlockOutputsIndicators();
    }, 2000); // Wait for 2 seconds after the last call
  }, [fetchBlockOutputsIndicators]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fetchIndicatorsRequestReference.current) {
        clearTimeout(fetchIndicatorsRequestReference.current);
      }
    };
  }, []);

  // Trigger fetch when baseScenario?.id changes (handles async loading)
  useEffect(() => {
    // Only trigger if baseScenario is loaded and has an id
    if (baseScenario?.id) {
      debouncedFetchIndicators();
    }
  }, [baseScenario?.id, debouncedFetchIndicators]);

  const handleAddDimIndicator = (indicatorName: string) => {
    dispatch(
      AddIndicator({
        blockId,
        data: {
          name: indicatorName,
          type: 'Input',
        },
        toaster: true,
      }),
    ).then((response: any) => {
      if (!response?.error) {
        // Find the empty row in tableData
        const emptyRowIndex = tableData.findIndex((row: any) => !row.id || row.isNewCell);

        // Create new indicator with correct structure
        const newIndicator = {
          id: response.payload.id,
          name: response.payload.name,
          help: '',
          type: 'Number',
          format: 'Number',
          format_properties: {
            scale: null,
            measure: null,
            null_value: '',
            decimal_points: 2,
            thousand_separators: true,
            negative_parentheses: false,
          },
          chart_type: 'line',
          dims: null,
          output_format: 'default',
          is_default_KPI: false,
          formula: null,
          calculated_formula: null,
          data_type: 'Number',
          is_favourite: true,
          indicator_type: 'input',
          data_input: {
            dimensions: [],
            block_id: blockId,
            time_dim: response.payload.time_dim,
          },
          warning: [],
          time_granularity: null,
          total_values: indicatorHeaders.map((header: any) => ({
            name: header.name,
            value: 0,
          })),
          total: 0,
          dim_item_data: null,
        };
        dispatch(handleLocallyAddIndicator(newIndicator));
        dispatch(handleSelectedCellLocally(newIndicator));
        dispatch(FetchFormulaList({ modelId, scenarioId: baseScenario?.id }));

        // Replace the empty row with new indicator
        setTableData((previousData: any) => {
          const newData = [...previousData];
          if (emptyRowIndex !== -1) {
            newData[emptyRowIndex] = newIndicator;
          }
          return newData;
        });

        // Update the row data with the API response
        const updatedRows = rows.map((row: any) => {
          if (row.isNewCell) {
            const updatedRow = {
              ...row,
              itemId: response.payload.id,
              itemName: response.payload.name,
              rowId: response.payload.id,
              isNewCell: false,
            };
            return updatedRow;
          }
          return row;
        });

        // Update rows state
        setRows(updatedRows);
        setRowsToRender(getExpandedRows(updatedRows));

        dispatch(FetchFormulaList({ modelId, scenarioId: baseScenario?.id }));
      }
    });
  };

  const handleRowsReorder = async (targetRowId: any, rowIds: any[]) => {
    const to = tableData?.findIndex((person: any) => person.id === targetRowId);
    const rowsIds = rowIds.map((id) => tableData?.findIndex((person: any) => person.id === id));
    const reorededArray = reorderArray(tableData, rowsIds, to);
    dispatch(
      ReorderIndicator({
        blockId,
        data: { indicators: reorededArray },
      }),
    ).then((response: any) => {
      if (!response?.error) debouncedFetchIndicators();
    });
  };

  const updateIndicator = (changes: any, change: any, columnId: any) => {
    dispatch(
      UpdateIndicator({
        indicatorId: changes[0]?.rowId,
        data: {
          name: change?.extraKey
            ? change?.checked
              ? 'subtotal'
              : 'default'
            : change?.checked !== undefined
            ? `${change?.checked}`
            : change?.text,
        },
        toaster: {
          errorMessage: '"An indicator with this already exists',
        },
      }),
    ).then(() => {
      dispatch(handleSelectedCell(selectedCell));
      dispatch(FetchFormulaList({ modelId, scenarioId: baseScenario?.id }));
      if (columnId === 'name') debouncedFetchIndicators();
    });
  };

  const onCellChange = (changes: any, columns_: any, setRows_: any) => {
    const change = changes[0];
    // If Escape key was pressed, clear focus and return without making API calls
    if (change?.type === 'text' && change?.newCell?.escapePressed) {
      setGridProperties({}); // This will clear focus from the current cell
      return;
    }
    if (change?.newCell?.isNewCell) {
      handleAddDimIndicator(change?.newCell?.text);
    } else if (change?.newCell?.text !== change?.previousCell?.text) {
      updateIndicator(changes, change?.newCell, change?.columnId);
    } else if (change?.type === 'chevron') {
      handleCellChange(changes, columns_, setRows_);
    }
  };

  const memoizedHandleContextMenu = useCallback(
    (
      selectedRowIds: string[],
      selectedColIds: string[],
      selectionMode: string,
      menuOptions: Array<{ id: string; label: string; handler?: () => void }>,
      selectedRanges: Array<Array<{ rowId: string; columnId: string }>>,
    ) => {
      if (
        selectedRanges[0] &&
        selectedRanges[0][0]?.rowId !== 'header' &&
        selectedRanges[0][0]?.rowId !== 'footer'
      ) {
        const deleteRow = {
          id: 'deleteRow',
          label: 'Delete row',
          handler: () => {
            const myRow = rows?.filter((data2: any) => {
              return data2?.rowId === selectedRanges[0][0]?.rowId && data2;
            });
            handleDeleteRow({ id: myRow[0]?.itemId, itemName: myRow[0]?.itemName });
          },
        };
        let selectedRows = false;
        for (let index = 0; index < selectedRanges[0]?.length; index += 1) {
          selectedRows = selectedRanges[0][0]?.rowId === selectedRanges[0][index]?.rowId;
        }
        if (selectedRanges[0][0]?.columnId !== 'delete' && selectedRows) {
          const filteredOptions = menuOptions.filter((object) => object.id !== 'cut');
          return [...filteredOptions, deleteRow];
        }
        return selectedRanges[0][0]?.columnId === 'delete' && selectedRanges[0]?.length === 1
          ? [deleteRow]
          : menuOptions.filter((object) => object.id !== 'cut');
      }
      return [];
    },
    [rows, handleDeleteRow],
  );

  const memoizedHandleCellChange = useCallback(
    (changes: any) => {
      onCellChange(changes, columns, setRows);
    },
    [columns, setRows, onCellChange],
  );

  const memoizedHandleColumnResize = useCallback(
    (ci: any, width: any) => {
      handleColumnResize(ci, width, setColumns);
      setTimeout(() => {
        dispatch(
          UpdateBlock({
            blockId,
            data: { indicator_column_size: Math.floor(width) },
          }),
        );
      }, 2000);
    },
    [blockId, dispatch, setColumns],
  );

  const handleLocationChanged = () => {
    if (Object.keys(gridProperties).length > 0) {
      setGridProperties({});
    }
    return true;
  };

  useEffect(() => {
    if (Object.keys(gridProperties).length > 0) {
      setGridProperties({});
    }
  }, [rows, columns]);

  // Optimize ReactGrid rendering
  const memoizedRowsToRender = useMemo(() => {
    return rowsToRender;
  }, [rowsToRender]);

  const memoizedColumns = useMemo(() => {
    return columns;
  }, [columns]);

  const handleDeleteModal = () => {
    setDeleteModal({ indicatorId: '', indicatorName: '', status: false });
    dispatch(
      DeleteIndicator({
        indicatorId: deleteModal.indicatorId,
        toaster: {
          successMessage: 'Indicator deleted',
        },
      }),
    ).then((response: any) => {
      if (!response?.error) {
        setGridProperties({
          focusLocation: { rowId: 'header', columnId: 'name' },
        });
        fetchBlockOutputsIndicators();
      }
    });
  };

  // Detect click outside the Flex and call debouncedFetchIndicators
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;

      // Check if click is within the table
      if (tableReference.current && !tableReference.current.contains(target)) {
        // Check if click is on a Chakra UI Menu component (MenuList, MenuItem, Switch, MenuButton)
        // to prevent triggering when BlockSetting dimensions are changed
        const isMenuClick =
          target.closest('[role="menu"]') ||
          target.closest('[role="menuitem"]') ||
          target.closest('[data-menu]') ||
          target.closest('button[aria-haspopup="menu"]') ||
          target.closest('[data-chakra-menu]') ||
          // Check if click is on a Switch component (Chakra UI Switch renders as a button with role="switch")
          target.closest('button[role="switch"]') ||
          // Check if click is on any element within a MenuItem (includes Switch inside MenuItem)
          (target.closest('input[type="checkbox"]') && target.closest('[role="menuitem"]'));

        // Only call debouncedFetchIndicators if it's not a menu click
        if (!isMenuClick) {
          debouncedFetchIndicators();
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tableReference, debouncedFetchIndicators]);

  if (blockOutputsLoading && rows?.length === 0) {
    return (
      <React.Fragment>
        <Flex direction='column' pb='1'>
          {[...new Array(6)].map((_, index) => (
            <Flex key={index} p={2} justifyContent={'space-between'}>
              {[...new Array(4)].map((__, subIndex: number) => (
                <Box key={subIndex} flex={1} mr={2}>
                  <Skeleton startColor='white' endColor='#CCD4E8' height='35px' width='100%' />
                </Box>
              ))}
            </Flex>
          ))}
        </Flex>
      </React.Fragment>
    );
  }

  if (blockOutputsError) {
    return (
      <React.Fragment>
        <Flex direction='column' alignItems='center' justifyContent='center' p={4}>
          <Text color='red.500'>{blockOutputsError}</Text>
        </Flex>
      </React.Fragment>
    );
  }

  if (!indicatorValues?.length && rows?.length === 0) {
    return (
      <React.Fragment>
        <Flex direction='column' alignItems='center' justifyContent='center' p={4}>
          <Box
            onClick={() => handleAddRow()}
            width={'100%'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            flexDirection={'column'}
            gap={2}
          >
            <Image h={30} w={30} src={addAction} transform='scaleX(-1)' />
            <Text color='white' cursor='pointer' textDecoration='underline' fontSize={'14px'}>
              Please click here to add indicator
            </Text>
          </Box>
        </Flex>
      </React.Fragment>
    );
  }

  if (!columns?.length || !rowsToRender?.length) {
    return (
      <React.Fragment>
        <FullScreenLoader
          width='100%'
          height={`calc(100vh - ${HEADER_HEIGHT_WITH_NAVIGATION}) - 45px`}
          bgColor='transparent'
        />
      </React.Fragment>
    );
  }
  return (
    <Flex
      height={'100%'}
      width={'100%'}
      flexDirection={'column'}
      ref={tableReference}
      flex={1}
      overflow={'hidden'}
    >
      {columns?.length && rows?.length && memoizedRowsToRender?.length && (
        <Box
          className='block-table-container sm-scroll'
          width={'100%'}
          margin={'initial'}
          maxH={'initial'}
          overflowY={rows.length > 6 ? 'auto' : 'initial'}
        >
          <ReactGrid
            rows={memoizedRowsToRender}
            columns={memoizedColumns}
            enableRowSelection={rows?.length > 3}
            stickyTopRows={rows?.length <= 6 ? 0 : 1}
            stickyRightColumns={columns?.length <= 2 ? 0 : 1}
            stickyLeftColumns={columns?.length <= 2 ? 0 : 1}
            stickyBottomRows={rows?.length <= 3 ? 0 : 1}
            verticalStickyBreakpoint={100}
            horizontalStickyBreakpoint={100}
            onRowsReordered={handleRowsReorder}
            canReorderRows={handleCanReorderRows}
            {...gridProperties}
            onFocusLocationChanging={handleLocationChanged}
            onFocusLocationChanged={handleFocusLocationChanged}
            onCellsChanged={memoizedHandleCellChange}
            onContextMenu={planPermission ? undefined : (memoizedHandleContextMenu as any)}
            onColumnResized={planPermission ? undefined : memoizedHandleColumnResize}
            customCellTemplates={{
              header: new HeaderRowTemplate(),
              delete: new DeleteRowCellTemplate(),
              text: new TextCellTemplateOpz(),
              date: new DateCellTemplate(),
              addRow: new AddRowCellTemplate(handleAddRow, planPermission, isProcessing),
              chevron: new ChevronCellTemplate(planPermission),
              dropdown: new DropdownCellTemplate(handleDeleteRow, planPermission),
            }}
          />
        </Box>
      )}
      <DeleteGroupModal
        isOpen={deleteModal.status}
        onClose={() => setDeleteModal({ indicatorId: '', indicatorName: '', status: false })}
        itemName={deleteModal.indicatorName || 'some name'}
        onDelete={() => handleDeleteModal()}
        orgChart={true}
        isBuilderMode={true}
      />
    </Flex>
  );
};

export default PlanPermission(BuilderModeDataTableOpz);
